import type { FC, FormEvent, ReactNode } from 'react'
import type { BagView } from '@/types'

import { useEffect, useRef, useState } from 'react'
import {
    ArrowLeft,
    ArrowRight,
    Check,
    LoaderCircle,
    Minus,
    Plus,
    ShoppingBag,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { firstName, formatPrice } from '@/lib/format'
import { isEmail } from '@/lib/validation'
import { useDialog } from '@/hooks/use-dialog'
import { useBag } from '@/components/generals/bag-provider'
import { ProductImage } from '@/components/generals/product-image'

const TITLES: Record<BagView, string> = {
    bag: "Your bag",
    reserve: "Private reservation",
    done: "Request received"
}

const EMPTY_FORM = {
    fullName: '',
    email: '',
    phone: '',
    city: '',
    note: '',
    company: ''
}

type ReservationResult = { ok: true, reference: string } | { ok: false, message: string }

const inputClass = 'h-12 w-full rounded-2xl border border-espresso/15 bg-white/70 px-4 text-[0.95rem] text-espresso placeholder:text-espresso/35 transition focus:border-gold-deep/60 focus:outline-none focus:ring-4 focus:ring-gold/20'
const labelClass = 'mb-1.5 block text-[0.68rem] uppercase tracking-[0.2em] text-espresso/70'

export const BagDrawer: FC = (): ReactNode => {
    const {
        items,
        count,
        subtotal,
        isOpen,
        view,
        closeBag,
        setView,
        setQuantity,
        removeItem,
        clear
    } = useBag()

    const panelRef = useRef<HTMLDivElement>(null)
    const closeRef = useRef<HTMLButtonElement>(null)
    const [form, setForm] = useState(EMPTY_FORM)
    const [sending, setSending] = useState(false)
    const [error, setError] = useState('')
    const [confirmation, setConfirmation] = useState<{ reference: string, name: string } | null>(null)

    useDialog(isOpen, closeBag, panelRef)

    // Moving between the bag, the form and the confirmation keeps focus inside the panel.
    useEffect(() => {
        if (!isOpen) return

        const frame = window.requestAnimationFrame(() => {
            const target = panelRef.current?.querySelector<HTMLElement>('[data-autofocus]') ?? closeRef.current
            target?.focus()
        })

        return () => window.cancelAnimationFrame(frame)
    }, [isOpen, view])

    const update = (field: keyof typeof EMPTY_FORM) => (value: string) => {
        setForm((current) => ({ ...current, [field]: value }))
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (sending) return

        if (items.length === 0) {
            setError("Your bag is empty. Add a piece from the collection first.")
            return
        }

        if (form.fullName.trim().length < 2) {
            setError("Please tell us your full name.")
            return
        }

        if (!isEmail(form.email.trim())) {
            setError("Please enter a valid email address.")
            return
        }

        setSending(true)
        setError('')

        let message = "We could not send your request just now. Please try again in a moment."

        try {
            const response = await fetch('/api/reservation', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...form,
                    items: items.map((item) => ({ slug: item.slug, quantity: item.quantity }))
                })
            })

            const result = (await response.json().catch(() => null)) as ReservationResult | null

            if (response.ok && result?.ok) {
                setConfirmation({ reference: result.reference, name: firstName(form.fullName) })
                setForm(EMPTY_FORM)
                clear()
                setView('done')
                setSending(false)
                return
            }

            if (result && !result.ok && result.message) message = result.message
        }
        catch {
            // Network failure: keep the general message.
        }

        setError(message)
        setSending(false)
    }

    return (
        <div
            aria-hidden={!isOpen}
            inert={!isOpen}
            className={cn('fixed inset-0 z-[60]', !isOpen && 'pointer-events-none')}
        >
            <div
                onClick={closeBag}
                className={cn(
                    'absolute inset-0 bg-espresso/35 backdrop-blur-[3px] transition-opacity duration-500',
                    isOpen ? 'opacity-100' : 'opacity-0'
                )}
            />

            <div
                ref={panelRef}
                role='dialog'
                aria-modal='true'
                aria-labelledby='bag-title'
                tabIndex={-1}
                className={cn(
                    'glass glass-frost absolute inset-y-2 right-2 flex w-[calc(100%-1rem)] max-w-[28rem] flex-col overflow-hidden rounded-[2rem] text-espresso transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] focus:outline-none sm:inset-y-3 sm:right-3',
                    isOpen ? 'translate-x-0' : 'translate-x-[calc(100%+1.5rem)]'
                )}
            >
                <div className='flex items-center justify-between gap-4 border-b border-espresso/10 px-6 py-5'>
                    <div>
                        <p className='text-[0.66rem] uppercase tracking-[0.3em] text-gold-deep'>
                            Maison Aveline
                        </p>

                        <h2 id='bag-title' className='mt-1 font-heading text-3xl font-normal'>
                            {TITLES[view]}
                        </h2>
                    </div>

                    <button
                        ref={closeRef}
                        type='button'
                        onClick={closeBag}
                        aria-label="Close your bag"
                        className='flex size-11 items-center justify-center rounded-full bg-white/60 ring-1 ring-espresso/10 transition-colors hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep'
                    >
                        <X className='size-5' strokeWidth={1.5} />
                    </button>
                </div>

                {view === 'bag' && items.length === 0 && (
                    <div className='flex flex-1 flex-col items-center justify-center px-8 text-center'>
                        <span className='flex size-16 items-center justify-center rounded-full bg-white/60 ring-1 ring-espresso/10'>
                            <ShoppingBag className='size-6 text-gold-deep' strokeWidth={1.3} />
                        </span>

                        <p className='mt-6 font-heading text-2xl'>
                            Your bag is waiting
                        </p>

                        <p className='mt-2 max-w-xs text-sm leading-relaxed text-muted-foreground'>
                            Choose a piece from the collection and it will be kept here until you are ready to reserve it.
                        </p>

                        <a
                            href='#collection'
                            onClick={closeBag}
                            className='mt-8 inline-flex h-12 items-center gap-2 rounded-full bg-espresso px-6 text-[0.75rem] uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep'
                        >
                            Discover the collection
                            <ArrowRight className='size-4' strokeWidth={1.5} />
                        </a>
                    </div>
                )}

                {view === 'bag' && items.length > 0 && (
                    <>
                        <ul className='flex-1 space-y-3 overflow-y-auto p-4 sm:p-5'>
                            {items.map((item) => (
                                <li key={item.slug} className='flex gap-4 rounded-[1.5rem] bg-white/55 p-3 ring-1 ring-espresso/5'>
                                    <div className='relative size-24 shrink-0 overflow-hidden rounded-2xl bg-sand'>
                                        <ProductImage src={item.image} alt={item.name} sizes='96px' />
                                    </div>

                                    <div className='flex min-w-0 flex-1 flex-col justify-between py-1'>
                                        <div className='flex items-start justify-between gap-3'>
                                            <div className='min-w-0'>
                                                <p
                                                    data-mdlfy-cms={item.cmsId ? `products:${item.cmsId}:category` : undefined}
                                                    className='text-[0.62rem] uppercase tracking-[0.22em] text-espresso/55'
                                                >
                                                    {item.category}
                                                </p>

                                                <p
                                                    data-mdlfy-cms={item.cmsId ? `products:${item.cmsId}:name` : undefined}
                                                    className='mt-1 truncate font-heading text-xl leading-tight'
                                                >
                                                    {item.name}
                                                </p>
                                            </div>

                                            <button
                                                type='button'
                                                onClick={() => removeItem(item.slug)}
                                                aria-label={`Remove ${item.name} from your bag`}
                                                className='-mr-1 -mt-1 flex size-8 shrink-0 items-center justify-center rounded-full text-espresso/50 transition-colors hover:bg-white hover:text-espresso focus-visible:outline-2 focus-visible:outline-gold-deep'
                                            >
                                                <X className='size-4' strokeWidth={1.5} />
                                            </button>
                                        </div>

                                        <div className='flex items-center justify-between gap-3'>
                                            <div className='flex items-center rounded-full bg-white/70 ring-1 ring-espresso/10'>
                                                <button
                                                    type='button'
                                                    onClick={() => setQuantity(item.slug, item.quantity - 1)}
                                                    disabled={item.quantity <= 1}
                                                    aria-label={`One fewer ${item.name}`}
                                                    className='flex size-8 items-center justify-center rounded-full transition-colors hover:bg-white disabled:opacity-30 focus-visible:outline-2 focus-visible:outline-gold-deep'
                                                >
                                                    <Minus className='size-3.5' strokeWidth={1.5} />
                                                </button>

                                                <span className='w-6 text-center text-sm tabular-nums'>
                                                    {item.quantity}
                                                </span>

                                                <button
                                                    type='button'
                                                    onClick={() => setQuantity(item.slug, item.quantity + 1)}
                                                    disabled={item.quantity >= 5}
                                                    aria-label={`One more ${item.name}`}
                                                    className='flex size-8 items-center justify-center rounded-full transition-colors hover:bg-white disabled:opacity-30 focus-visible:outline-2 focus-visible:outline-gold-deep'
                                                >
                                                    <Plus className='size-3.5' strokeWidth={1.5} />
                                                </button>
                                            </div>

                                            <p className='font-heading text-lg'>
                                                {formatPrice(item.price * item.quantity)}
                                            </p>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        <div className='border-t border-espresso/10 px-6 py-5'>
                            <div className='flex items-baseline justify-between gap-4'>
                                <p className='text-sm text-muted-foreground'>
                                    Subtotal
                                </p>

                                <p className='font-heading text-3xl'>
                                    {formatPrice(subtotal)}
                                </p>
                            </div>

                            <p className='mt-2 text-xs leading-relaxed text-muted-foreground'>
                                Complimentary insured delivery and returns. Payment is arranged personally by your client advisor, nothing is charged online.
                            </p>

                            <button
                                type='button'
                                onClick={() => {
                                    setError('')
                                    setView('reserve')
                                }}
                                className='mt-5 flex h-13 w-full items-center justify-center gap-2 rounded-full bg-espresso text-[0.78rem] uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep'
                            >
                                Request a private reservation
                                <ArrowRight className='size-4' strokeWidth={1.5} />
                            </button>
                        </div>
                    </>
                )}

                {view === 'reserve' && (
                    <form onSubmit={handleSubmit} noValidate className='flex min-h-0 flex-1 flex-col'>
                        <div className='flex-1 space-y-4 overflow-y-auto px-6 py-5'>
                            <div className='flex items-center justify-between rounded-2xl bg-white/55 px-4 py-3 ring-1 ring-espresso/5'>
                                <span className='text-sm text-muted-foreground'>
                                    {count} {count === 1 ? 'piece' : 'pieces'} in your bag
                                </span>

                                <span className='font-heading text-xl'>
                                    {formatPrice(subtotal)}
                                </span>
                            </div>

                            <p className='text-sm leading-relaxed text-muted-foreground'>
                                Leave your details and a client advisor will contact you within one working day to confirm availability, payment and delivery.
                            </p>

                            <div>
                                <label htmlFor='reserve-name' className={labelClass}>Full name</label>
                                <input
                                    id='reserve-name'
                                    data-autofocus
                                    autoComplete='name'
                                    required
                                    value={form.fullName}
                                    onChange={(event) => update('fullName')(event.target.value)}
                                    className={inputClass}
                                    placeholder="Your name"
                                />
                            </div>

                            <div>
                                <label htmlFor='reserve-email' className={labelClass}>Email</label>
                                <input
                                    id='reserve-email'
                                    type='email'
                                    autoComplete='email'
                                    required
                                    value={form.email}
                                    onChange={(event) => update('email')(event.target.value)}
                                    className={inputClass}
                                    placeholder="you@example.com"
                                />
                            </div>

                            <div className='grid gap-4 sm:grid-cols-2'>
                                <div>
                                    <label htmlFor='reserve-phone' className={labelClass}>Phone (optional)</label>
                                    <input
                                        id='reserve-phone'
                                        type='tel'
                                        autoComplete='tel'
                                        value={form.phone}
                                        onChange={(event) => update('phone')(event.target.value)}
                                        className={inputClass}
                                    />
                                </div>

                                <div>
                                    <label htmlFor='reserve-city' className={labelClass}>City</label>
                                    <input
                                        id='reserve-city'
                                        autoComplete='address-level2'
                                        value={form.city}
                                        onChange={(event) => update('city')(event.target.value)}
                                        className={inputClass}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor='reserve-note' className={labelClass}>A note for your advisor (optional)</label>
                                <textarea
                                    id='reserve-note'
                                    rows={3}
                                    value={form.note}
                                    onChange={(event) => update('note')(event.target.value)}
                                    className={cn(inputClass, 'h-auto resize-none py-3')}
                                    placeholder="An engraving, a date to keep in mind, a preferred time to call"
                                />
                            </div>

                            <div aria-hidden='true' className='hidden'>
                                <label htmlFor='reserve-company'>Company</label>
                                <input
                                    id='reserve-company'
                                    tabIndex={-1}
                                    autoComplete='off'
                                    value={form.company}
                                    onChange={(event) => update('company')(event.target.value)}
                                />
                            </div>

                            {error && (
                                <p role='alert' className='rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive'>
                                    {error}
                                </p>
                            )}
                        </div>

                        <div className='flex gap-3 border-t border-espresso/10 px-6 py-5'>
                            <button
                                type='button'
                                onClick={() => setView('bag')}
                                aria-label="Back to your bag"
                                className='flex size-13 shrink-0 items-center justify-center rounded-full bg-white/60 ring-1 ring-espresso/10 transition-colors hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep'
                            >
                                <ArrowLeft className='size-5' strokeWidth={1.5} />
                            </button>

                            <button
                                type='submit'
                                disabled={sending || items.length === 0}
                                className='flex h-13 flex-1 items-center justify-center gap-2 rounded-full bg-espresso text-[0.78rem] uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-ink disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep'
                            >
                                {sending && <LoaderCircle className='size-4 animate-spin' strokeWidth={1.5} />}
                                {sending ? "Sending" : "Send my request"}
                            </button>
                        </div>
                    </form>
                )}

                {view === 'done' && (
                    <div className='flex flex-1 flex-col items-center justify-center px-8 text-center'>
                        <span className='flex size-16 items-center justify-center rounded-full bg-espresso text-champagne animate-in zoom-in duration-500'>
                            <Check className='size-7' strokeWidth={1.5} />
                        </span>

                        <p className='mt-6 font-heading text-3xl'>
                            {confirmation?.name ? `Thank you, ${confirmation.name}.` : "Thank you."}
                        </p>

                        <p className='mt-3 max-w-xs text-sm leading-relaxed text-muted-foreground'>
                            Your request is with our client advisors in Paris. We will be in touch within one working day to confirm availability, payment and delivery.
                        </p>

                        {confirmation?.reference && (
                            <p className='mt-6 rounded-full bg-white/60 px-4 py-2 text-[0.7rem] uppercase tracking-[0.22em] ring-1 ring-espresso/10'>
                                Reference <span className='font-medium text-espresso'>{confirmation.reference}</span>
                            </p>
                        )}

                        <button
                            type='button'
                            data-autofocus
                            onClick={closeBag}
                            className='mt-8 inline-flex h-12 items-center rounded-full bg-espresso px-6 text-[0.75rem] uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-deep'
                        >
                            Continue browsing
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
